import { CloudUpload, LoaderCircle, TriangleAlert, Wifi, WifiOff } from 'lucide-react';
import { Link } from 'react-router';
import { useOutboxStatus } from '@/lib/outbox';
import { useOnline } from '@/lib/use-online';
import { cn } from '@/lib/utils';

export interface SyncIndicatorViewProps {
  online: boolean;
  /** Operations waiting in the offline outbox. */
  pending: number;
  /** Operations refused by the server (e.g. 409), waiting for "riprova / scarta". */
  failed: number;
  flushing?: boolean;
  /** Icon only (narrow headers). */
  compact?: boolean;
  className?: string;
}

/** Online/offline state and outbox counters (docs/08 §7); opens /sync. */
export function SyncIndicatorView({
  online,
  pending,
  failed,
  flushing = false,
  compact = false,
  className,
}: SyncIndicatorViewProps) {
  const label = `Sincronizzazione: ${online ? 'online' : 'offline'}, ${pending} in coda, ${failed} ${failed === 1 ? 'errore' : 'errori'}`;
  return (
    <Link
      to="/sync"
      className={cn(
        'inline-flex h-11 items-center gap-2 rounded-full border px-3 text-sm font-medium',
        failed > 0
          ? 'border-danger/40 bg-danger-soft text-danger'
          : online
            ? 'border-border bg-background text-foreground'
            : 'border-warning/40 bg-warning-soft text-warning',
        className,
      )}
      aria-label={label}
      title={label}
    >
      {flushing ? (
        <LoaderCircle className="size-4 animate-spin" aria-hidden />
      ) : online ? (
        <Wifi className="size-4 text-success" aria-hidden />
      ) : (
        <WifiOff className="size-4" aria-hidden />
      )}
      {compact ? null : <span>{online ? 'Online' : 'Offline'}</span>}
      {pending > 0 ? (
        <span className="inline-flex items-center gap-1">
          <CloudUpload className="size-4" aria-hidden />
          {pending}
        </span>
      ) : null}
      {failed > 0 ? (
        <span className="inline-flex items-center gap-1">
          <TriangleAlert className="size-4" aria-hidden />
          {failed}
        </span>
      ) : null}
    </Link>
  );
}

/** Indicator wired to the browser connectivity and to the outbox. */
export function SyncIndicator({ compact, className }: { compact?: boolean; className?: string }) {
  const online = useOnline();
  const { pending, failed, flushing } = useOutboxStatus();
  return (
    <SyncIndicatorView
      online={online}
      pending={pending}
      failed={failed}
      flushing={flushing}
      compact={compact}
      className={className}
    />
  );
}
